/**
 * 포인트·출금·인증 정책값.
 *
 * ★ 지금은 프론트 상수지만 **정본은 백엔드다.** 아래 값들은 전부 서버 어딘가에
 *   같은 값이 또 적혀 있고, 어긋나면 "화면엔 300P 라고 써 있는데 200P 가 들어왔다"
 *   같은 형태로 드러난다. 정책 API 가 생기면 이 모듈만 fetch 로 바꾸면 된다.
 *
 * | 이 모듈                  | 백엔드 정본                                        |
 * | ------------------------ | -------------------------------------------------- |
 * | `SIGNUP_POINT`           | `Core/Vars/Point.php::UserRegist['amount']`        |
 * | `INFO_STEPS[].amount`    | `Core/Vars/Point.php::UserUpdate/Mobile/Marketing` |
 * | `POINT_EXPIRE_YEARS`     | 같은 파일의 `'expire' => '1 years'`                |
 * | `MOBILE_*`               | `ControllerApiUser::MOBILE_CODE_TTL` 등            |
 * | `WITHDRAW_MINIMUM_POINT` | ❗ 백엔드에 아직 없다 (출금 API 미구현)            |
 *
 * 화면 문구는 여기서 계산한다. 숫자를 JSX 안에 직접 쓰면 정책이 바뀔 때
 * 반드시 한 군데가 빠진다 (실제로 `login` 화면이 500P 로 남아 있었다).
 */

/** 추가정보 입력 단계 한 칸 */
export interface InfoStepPolicy {
  /** 단계 번호 (1~3) */
  step: number;
  /** 포인트 원장 코드 — `MC_USER_POINT.F_REWARD_CODE` */
  code: string;
  /** 적립 내역에 남는 이름 */
  name: string;
  /** 적립액 */
  amount: number;
}

/**
 * 신규 회원가입 보상 — `USER.REGIST`.
 *
 * ★ 2026-09-09 정책 변경으로 **0P** 다. 코드는 남아 있다 — 정책 변경 전에 500P 를
 *   받은 회원의 내역이 이 코드로 남아 있기 때문이다.
 */
export const SIGNUP_POINT = 0;

/**
 * 추가정보 입력 3단계.
 *
 * ★ 2026-09-09 정책 변경 — **마케팅 동의만 적립한다.**
 *   생년월일·성별과 휴대폰 번호는 0P 이지만 단계 자체는 그대로 받는다.
 *   금액이 0인 단계는 서버가 포인트 원장에 아무것도 남기지 않는다.
 *
 * ★ 1단계 코드가 `USER.UPDATE` 인 것은 의도된 것이다. 정책 변경 전에 이 코드로
 *   적립받은 회원이 또 받는 것을 막는다(중복 체크가 `F_REWARD_CODE` 단위).
 */
export const INFO_STEPS: InfoStepPolicy[] = [
  { step: 1, code: 'USER.UPDATE', name: '생년월일·성별 입력', amount: 0 },
  { step: 2, code: 'USER.MOBILE', name: '휴대폰 번호 입력', amount: 0 },
  { step: 3, code: 'USER.MARKETING', name: '마케팅 수신 동의 보상', amount: 500 },
];

/** 추가정보 단계 수 (진행 표시 `n / 3`) */
export const INFO_TOTAL_STEPS = INFO_STEPS.length;

/** 추가정보를 끝까지 입력했을 때 받는 총액 — 현재 500P */
export const INFO_TOTAL_POINT = INFO_STEPS.reduce((sum, s) => sum + s.amount, 0);

/** 단계 번호로 정책을 찾는다. */
export function infoStep(step: number): InfoStepPolicy {
  const found = INFO_STEPS.find((s) => s.step === step);
  if (!found) throw new Error(`알 수 없는 추가정보 단계: ${step}`);
  return found;
}

/** 이 단계에 적립이 붙는가. 0P 단계는 문구에서 포인트를 빼야 한다. */
export function hasReward(step: number): boolean {
  return infoStep(step).amount > 0;
}

/** 단계 번호 → 포인트 코드. 서버가 내려주는 `requestDetailSteps` 와 맞춘다. */
export const INFO_STEP_CODE: Record<number, string> = Object.fromEntries(
  INFO_STEPS.map((s) => [s.step, s.code])
);

/** 포인트 유효기간(년) */
export const POINT_EXPIRE_YEARS = 1;

/**
 * 출금 최소 포인트.
 *
 * ⚠️ 시안 `_mypage.html` 의 안내 문구에서 가져온 값이다. 백엔드에는 아직 출금
 * 로직이 없어(`/mypage/withdraw` 미이식) 대조할 정본이 없다. 출금 API 를 만들 때
 * 서버 값과 반드시 맞출 것.
 */
export const WITHDRAW_MINIMUM_POINT = 5000;

/**
 * 한 번에 신청할 수 있는 최대 포인트.
 *
 * 정본은 백엔드 `ModuleWithdraw::MAXIMUM_POINT` 이고 출금 API 가 `policy.maximum`
 * 으로 내려준다. 여기 값은 응답이 오기 전 화면을 그릴 때만 쓴다.
 */
export const WITHDRAW_MAXIMUM_POINT = 50000;

////////////////////////////////////////////////////////////////////////////////
/// 휴대폰 SMS 인증 — 백엔드 `ControllerApiUser` 상수와 짝을 맞춘다.
/// 서버가 응답으로 실제 값을 돌려주므로 여기 값은 응답이 없을 때의 대비책이다.
////////////////////////////////////////////////////////////////////////////////

/** 인증번호 자릿수 */
export const MOBILE_CODE_LENGTH = 6;
/** 인증번호 유효시간(초) — `MOBILE_CODE_TTL` */
export const MOBILE_CODE_TTL_SEC = 180;
/** 재발송 대기(초) — `MOBILE_RESEND_COOLDOWN` */
export const MOBILE_RESEND_COOLDOWN_SEC = 30;
